import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, ShieldCheck, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHead, Panel, Stat, inputCls } from "@/components/kit";
import { API, api, errMsg } from "@/lib/api";

function Interested({ onChange }) {
  const [q, setQ] = useState("");
  const [r, setR] = useState(null);
  const search = () => api.get("/privacy/search", { params: { q } }).then((x) => setR(x.data)).catch((e) => toast.error(errMsg(e)));
  const erase = async () => {
    if (!window.confirm(`Cancellare i dati di «${q}»? Richieste e preventivi non firmati vengono anonimizzati, le conferme invitati eliminate e i documenti d'identità cancellati. I contratti firmati restano per obbligo di legge (fiscale/civilistico).`)) return;
    try {
      const x = (await api.post("/privacy/erase", { q })).data;
      toast.success(`Fatto: ${x.richieste} richieste, ${x.preventivi} preventivi, ${x.conferme} conferme, ${x.documenti} documenti`);
      search();
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const n = r ? r.contracts.length + r.quotes.length + r.leads.length + r.rsvp : 0;
  return (
    <Panel title="Richieste dell'interessato (accesso, copia, cancellazione)" testId="privacy-subject">
      <div className="flex gap-2"><input className={inputCls} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Nome, telefono o email" data-testid="privacy-q" /><Button className="rounded-full" onClick={search} data-testid="privacy-search">Cerca</Button></div>
      {r && (
        <div className="space-y-2 text-sm" data-testid="privacy-results">
          <div>{r.contracts.length} contratti • {r.quotes.length} preventivi • {r.leads.length} richieste • {r.rsvp} conferme invitati</div>
          {r.contracts.map((c) => <div key={c.id} className="text-xs text-[#47606B]">Contratto {c.number} • {c.nome} • {c.status}</div>)}
          {n > 0 && <div className="flex flex-wrap gap-2"><a href={`${API}/privacy/export?q=${encodeURIComponent(q)}`} onClick={() => setTimeout(onChange, 1500)} className="inline-flex items-center rounded-full border border-[#D4E6EC] px-4 py-2" data-testid="privacy-export"><Download size={14} className="mr-1" /> Scarica i suoi dati</a><Button variant="outline" className="rounded-full text-[#991B1B]" onClick={erase} data-testid="privacy-erase"><Trash2 size={14} className="mr-1" /> Cancella i suoi dati</Button></div>}
        </div>
      )}
    </Panel>
  );
}

export default function Privacy() {
  const [o, setO] = useState(null);
  const [m, setM] = useState(24);
  const load = () => api.get("/privacy/overview").then((r) => { setO(r.data); setM(r.data.retention_months); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);
  if (!o) return <div className="text-sm text-[#7B929C]">Caricamento…</div>;
  const saveM = () => api.put("/privacy/settings", { id_retention_months: m }).then(() => { toast.success("Salvato"); load(); }).catch((e) => toast.error(errMsg(e)));
  const purge = async () => {
    if (!window.confirm(`Cancellare ${o.expired_ids} documenti d'identità di eventi precedenti al ${o.limit_date.split("-").reverse().join("/")}?`)) return;
    try {
      const r = (await api.post("/privacy/purge")).data;
      toast.success(`${r.deleted} documenti cancellati`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    load();
  };
  return (
    <div className="space-y-6">
      <PageHead eyebrow="Regolamento UE 2016/679" title="Privacy" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat dark label="Documenti d'identità conservati" value={o.id_files} testId="privacy-ids" />
        <Stat label="Da cancellare (scaduti)" value={o.expired_ids} testId="privacy-expired" />
        <Stat label="Firme con informativa" value={o.consents.firme_con_informativa} testId="privacy-consents" />
        <Stat label="Consensi richieste online" value={o.consents.richieste_online} sub={`${o.consents.conferme_invitati} conferme invitati`} />
      </div>
      <Panel title="Conservazione dei documenti d'identità" testId="privacy-retention">
        <p className="text-sm text-[#47606B] -mt-2">I documenti servono per il contratto: dopo l'evento vanno tenuti solo per il tempo necessario. Contratti e ricevute restano (obbligo fiscale di 10 anni).</p>
        <div className="flex flex-wrap items-center gap-2 text-sm">Cancella i documenti <input type="number" min="1" max="120" className="w-20 rounded-md border border-[#D4E6EC] px-2 py-1.5 text-sm" value={m} onChange={(e) => setM(e.target.value)} data-testid="privacy-months" /> mesi dopo l'evento <Button size="sm" variant="outline" className="rounded-full" onClick={saveM} data-testid="privacy-save">Salva</Button></div>
        <Button className="rounded-full" disabled={!o.expired_ids} onClick={purge} data-testid="privacy-purge"><Trash2 size={14} className="mr-1" /> Cancella ora i {o.expired_ids} documenti scaduti</Button>
      </Panel>
      <Interested onChange={load} />
      <Panel title="Chi ha visto cosa" testId="privacy-log">
        {!o.access_log.length ? <div className="text-sm text-[#7B929C]">Nessun accesso registrato.</div> : <div className="max-h-80 overflow-y-auto divide-y divide-[#EAF5F7] text-sm">{o.access_log.map((x, i) => <div key={i} className="py-1.5 flex justify-between gap-2"><span><ShieldCheck size={13} className="inline mr-1 text-[#34B3B0]" />{x.azione} • {x.oggetto}</span><span className="text-xs text-[#7B929C] text-right">{x.by}<br />{new Date(x.at).toLocaleString("it-IT")}</span></div>)}</div>}
      </Panel>
    </div>
  );
}
